import { alpha, Box, Divider, Grid, Paper, Typography } from "@mui/material";
import { ScoreRating } from "~/operations";
import { useGetImpactColorUpdated } from "~/components/impact/Updated/useGetImpactColorUpdated";
import { Flex } from "~/components/Flex";

type Stat = {
  label: string;
  value: number;
};

type PlatformCheckScoreBlockProps = {
  rating: ScoreRating;
  stats: Stat[];
};

export function PlatformCheckScoreBlock({
  rating,
  stats,
}: PlatformCheckScoreBlockProps) {
  const color = useGetImpactColorUpdated(rating);

  const ratingLabels: Record<ScoreRating, string> = {
    [ScoreRating.Critical]: "CRITICAL",
    [ScoreRating.Error]: "ERROR",
    [ScoreRating.High]: "HIGH",
    [ScoreRating.Medium]: "MEDIUM",
    [ScoreRating.Low]: "LOW",
    [ScoreRating.None]: "NONE",
    [ScoreRating.Unscored]: "UNSCORED",
  };

  const ratingDots = () => {
    const dotCount = {
      [ScoreRating.Critical]: 5,
      [ScoreRating.Error]: 0,
      [ScoreRating.High]: 4,
      [ScoreRating.Medium]: 3,
      [ScoreRating.Low]: 2,
      [ScoreRating.None]: 1,
      [ScoreRating.Unscored]: 0,
    };

    const count = dotCount[rating] || 0;

    return (
      <Flex gap={1} justifyContent="center" my={1}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: index < count ? color : alpha(color, 0.2),
            }}
          />
        ))}
      </Flex>
    );
  };

  return (
    <Grid
      component={Paper}
      sx={{
        display: "block",
        p: 4,
        width: {
          xs: "100%",
          sm: 360,
          md: "100%",
        },
        backgroundColor: "background.paper",
      }}
      size={12}
    >
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          {ratingLabels[rating]}
        </Typography>
        {ratingDots()}
      </Box>

      <Divider sx={{ mb: 2, borderBottomWidth: 2, borderColor: "#F0F0F0" }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {stats.map((stat, index) => (
          <Box key={index}>
            <Flex justifyContent="space-between" alignItems="center">
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: "text.primary",
                  lineHeight: 1,
                }}
              >
                {stat.value}
              </Typography>
            </Flex>
          </Box>
        ))}
      </Box>
    </Grid>
  );
}
